import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CircularProgress } from '@fluent/ui';
import type { Lesson } from '@fluent/types';
import { getLesson } from '../content/lessons.js';
import { isPhrasesLessonId } from '../content/course.js';
import { usePhrasesContent } from '../content/usePhrasesContent.js';
import { useCompletions } from '../progress/hooks.js';
import { useAuth } from '../auth/authStore.js';
import './DailyGoalRing.css';

/** Average seconds per exercise — rough constant for estimating lesson time
 *  without instrumenting actual user pace. Tuned for an A1 beginner who
 *  pauses to read + replay audio. Refine when Phase 13 analytics data shows
 *  real per-exercise times. */
const SECONDS_PER_EXERCISE = 25;

/** Convert a Lesson to its estimated wall-clock duration in seconds. */
function estimateLessonSeconds(lesson: Lesson): number {
  return lesson.exercises.length * SECONDS_PER_EXERCISE;
}

/** YYYY-MM-DD in the user's timezone — same shape SQL uses (`v_today`). */
function todayInTz(tz: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());
  const y = parts.find((p) => p.type === 'year')?.value ?? '1970';
  const m = parts.find((p) => p.type === 'month')?.value ?? '01';
  const d = parts.find((p) => p.type === 'day')?.value ?? '01';
  return `${y}-${m}-${d}`;
}

function isCompletedToday(completedAt: string, tz: string): boolean {
  const completedDate = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(completedAt));
  return completedDate === todayInTz(tz);
}

export interface DailyGoalRingProps {
  /** Override the diameter. Default 56 to fit the topbar nicely. */
  size?: number;
}

/**
 * DailyGoalRing — circular progress against the user's daily-goal-minutes
 * target. Sits in the home topbar next to the streak flame. Computes
 * today's minutes from completed lessons (exercise-count × 25s heuristic)
 * and renders as a ring + "X / N" label.
 *
 * No daily-goal set OR no completions yet → renders an empty ring with
 * the goal as the denominator (gives the user a target to aim at).
 */
export function DailyGoalRing({ size = 56 }: DailyGoalRingProps) {
  const { t } = useTranslation();
  const { profile } = useAuth();
  const { data: completions = {} } = useCompletions();

  const goalMinutes = profile?.daily_goal_minutes ?? 10;
  const tz = profile?.timezone ?? 'Asia/Tbilisi';

  const today = useMemo(
    () => Object.values(completions).filter((row) => isCompletedToday(row.completed_at, tz)),
    [completions, tz],
  );
  // A phrases lesson done today counts too, but its exercises live in the
  // lazy phrases lessons (content/lessons.ts). Fetch them in the background
  // ONLY then — Home never downloads them for anyone else — and count the
  // lesson once they land. Until then (or if the fetch fails) it is left
  // out: the ring never waits and never breaks on it.
  // (The hook re-renders this ring when they arrive; the count below is
  // read fresh on every render — a handful of rows.)
  const needsPhrases = today.some((row) => isPhrasesLessonId(row.lesson_id));
  usePhrasesContent(needsPhrases);

  let totalSec = 0;
  for (const row of today) {
    const lesson = getLesson(row.lesson_id);
    if (!lesson) continue;
    totalSec += estimateLessonSeconds(lesson);
  }
  const todayMinutes = Math.round(totalSec / 60);

  const value = goalMinutes > 0 ? todayMinutes / goalMinutes : 0;
  const isComplete = todayMinutes >= goalMinutes && goalMinutes > 0;

  return (
    <div
      className="fluent-daily-goal"
      data-complete={isComplete || undefined}
      title={t('home.daily_goal.tooltip', { current: todayMinutes, target: goalMinutes })}
    >
      <CircularProgress
        value={value}
        size={size}
        tone="primary"
        ariaLabel={t('home.daily_goal.aria', { current: todayMinutes, target: goalMinutes })}
        label={
          <span className="fluent-daily-goal__label">
            <span className="fluent-daily-goal__current">{todayMinutes}</span>
            <span className="fluent-daily-goal__sep">/</span>
            <span className="fluent-daily-goal__target">{goalMinutes}</span>
          </span>
        }
      />
    </div>
  );
}
